/**
 * CwUnifiedList — flat list of Connected Search hits.
 *
 * Sprint CW-G TKT-1. The Unified layout option in the global search
 * dropdown — one ordered list, no per-provider grouping.
 */
import { type FC, type MouseEvent } from 'react';
import CwResultRow, { type CwSearchHit } from './CwResultRow';

interface Props {
  hits: CwSearchHit[];
  limit?: number;
  activeId?: string | null;
  idPrefix: string;
  onSelect: (hit: CwSearchHit, event: MouseEvent) => void;
  onHoverEnter?: (hit: CwSearchHit, e: { clientX: number; clientY: number }) => void;
  onHoverMove?: (e: { clientX: number; clientY: number }) => void;
  onHoverLeave?: () => void;
}

const CwUnifiedList: FC<Props> = ({
  hits,
  limit = 10,
  activeId,
  idPrefix,
  onSelect,
  onHoverEnter,
  onHoverMove,
  onHoverLeave,
}) => {
  const visible = hits.slice(0, limit);
  const overflow = Math.max(0, hits.length - visible.length);

  if (visible.length === 0) return null;
  return (
    <ul className="cw-unified-list" role="presentation">
      {visible.map((hit) => {
        const optionId = `${idPrefix}-${hit.id}`;
        return (
          <li key={hit.id} role="presentation">
            <CwResultRow
              hit={hit}
              id={optionId}
              active={activeId === optionId}
              onSelect={onSelect}
              onHoverEnter={onHoverEnter}
              onHoverMove={onHoverMove}
              onHoverLeave={onHoverLeave}
            />
          </li>
        );
      })}
      {overflow > 0 && (
        <li className="cw-unified-list__overflow" role="presentation">
          + {overflow} more — open Connected Search to see all
        </li>
      )}
    </ul>
  );
};

export default CwUnifiedList;
